import React, { useEffect, useState } from "react";
import Grid from "@mui/material/Grid";
import DropdownField from "./DropdownField";
import { VIPSDataServiceURL, requestOptions } from "../../Constants/GlobalConstant";

/**
 * DeliveryPriorityDropdown
 * Fetches Order/GetOrderPriorities and renders a dropdown labeled "Select Delivery".
 * Renders only when API returns { required: true }.
 * Options map Priority_Id -> Code, Priority_Name -> Description.
 */
const DeliveryPriorityDropdown = ({ divisionId, storeGroup, labels = [], onChange, onRequiredChange }) => {
  const [show, setShow] = useState(false);
  const [options, setOptions] = useState([]);
  const [value, setValue] = useState("");

  useEffect(() => {
    let isMounted = true;
    const fetchPriorities = async () => {
      try {
        const body = {
          divisionId: divisionId || "",
          storeGroup: storeGroup || "",
          Labels: Array.isArray(labels) ? labels : [],
        };
        const opts = {
          ...requestOptions,
          headers: {
            ...requestOptions.headers,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(body),
        };
        const resp = await fetch(
          `${VIPSDataServiceURL}/Order/GetOrderPriorities`,
          opts
        );
        const data = await resp.json();
        const required = !!data?.required;
        const list = Array.isArray(data?.orderPriorities)
          ? data.orderPriorities
          : [];
        if (!isMounted) return;
        setShow(required);
        const mapped = list
          .map((p) => ({
            Code: String(p?.Priority_Id ?? ""),
            Description: String(p?.Description ?? ""),
            isDefault: String(p?.Is_Default ?? "N").toUpperCase() === "Y",
            Priority_Order: Number(p?.Priority_Order ?? 0),
            Days_Required: Number(p?.Days_Required ?? 0),
          }))
          // remove any empty/invalid options
          .filter((o) => o.Code && o.Description)
          .sort((a, b) => a.Priority_Order - b.Priority_Order);
        setOptions(mapped);
        if (required) {
          const def = mapped.find((x) => x.isDefault) || mapped.find((x) => x.Code !== "0") || mapped[0];
          const defCode = def ? def.Code : "";
          setValue(defCode);
          if (def && typeof onChange === "function") {
            onChange({ priorityId: def.Code, daysRequired: def.Days_Required, required });
          }
          if (typeof onRequiredChange === "function") {
            onRequiredChange({
              required,
              defaultDays: def?.Days_Required ?? null,
              defaultPriorityId: def?.Code ?? "",
            });
          }
        } else {
          setValue("");
          if (typeof onRequiredChange === "function") {
            onRequiredChange({ required, defaultDays: 0, defaultPriorityId: 0 });
          }
        }
      } catch (e) {
        // On error, do not render the control
        if (!isMounted) return;
        setShow(false);
        setOptions([]);
        setValue("");
      }
    };
    fetchPriorities();
    return () => {
      isMounted = false;
    };
  }, [divisionId, storeGroup]);
  if (!show) return null;

  return (
    <Grid item xs={12} sm={6} md={4}>
      <DropdownField
        label="Select Delivery Priority"
        value={value}
        onChange={(e) => {
          const v = e.target.value;
          setValue(v);
          const found = options.find((o) => o.Code === v);
          if (found && typeof onChange === "function") {
            onChange({ priorityId: found.Code, daysRequired: found.Days_Required, required: show });
          }
        }}
        options={options}
        disabled={false}
        includeEmptyOption={false}
      />
    </Grid>
  );
};

export default DeliveryPriorityDropdown;
